import React, { useEffect, useState } from "react";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import { message } from "antd";
import { FaArrowLeft, FaFileExcel } from "react-icons/fa6";

import Header from "../../components/Header";
import {
  exportarRelatorioAnaliseAcessos,
  relatorioAnaliseAcessos,
} from "../../services/http/dashboardService";
import { saveAxiosBlobAs } from "../../helpers/saveAxiosBlobAs";
import {
  FiltrosAnalise,
  filtrosDaQuery,
  filtrosParaQuery,
  formatarData,
  formatarNumero,
  periodoValido,
} from "./FiltrosAnalise";
import "../Admin/styles.css";
import "./styles.scss";

// "Ver relatório completo" da Análise de Acessos (RD 94908).

const ITENS_POR_PAGINA = 10;

const RELATORIOS = {
  pastas: { titulo: "Pastas mais acessadas", coluna: "Pasta", unidade: "Acessos", caminho: true },
  arquivos: { titulo: "Arquivos mais acessados (geral)", coluna: "Arquivo", unidade: "Acessos", caminho: true },
  dealers: { titulo: "Dealers que mais baixam", coluna: "Dealer", unidade: "Downloads" },
  perfis: { titulo: "Acessos por perfil", coluna: "Perfil", unidade: "Acessos", percentual: true },
};

export default function RelatorioAnaliseAcessos() {
  const { tipo } = useParams();
  const location = useLocation();
  const history = useHistory();
  const relatorio = RELATORIOS[tipo];

  const [filtros, setFiltros] = useState(() => filtrosDaQuery(location.search));
  const [filtrosAplicados, setFiltrosAplicados] = useState(filtros);
  const [lista, setLista] = useState([]);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [totalItens, setTotalItens] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [exportando, setExportando] = useState(false);

  const carregar = async (consulta, pagina) => {
    if (!periodoValido(consulta)) {
      message.warning("Informe um período válido: a data final deve ser maior ou igual à inicial.");
      return;
    }

    setCarregando(true);
    const resposta = await relatorioAnaliseAcessos(tipo, consulta, pagina, ITENS_POR_PAGINA);
    setCarregando(false);

    if (resposta.fail) {
      message.error(resposta.errors?.message || "Não foi possível carregar o relatório");
      return;
    }

    setLista(resposta.data?.data ?? []);
    setTotalItens(resposta.data?.totalCount ?? 0);
    setTotalPaginas(resposta.data?.totalPages ?? 0);
    setPaginaAtual(pagina);
    setFiltrosAplicados(consulta);
  };

  useEffect(() => {
    if (relatorio) carregar(filtros, 1);
  }, [tipo]);

  const aoAtualizar = () => {
    history.replace(`/dashboard/relatorio/${tipo}?${filtrosParaQuery(filtros)}`);
    carregar(filtros, 1);
  };

  const aoBaixarExcel = async () => {
    try {
      setExportando(true);
      const resposta = await exportarRelatorioAnaliseAcessos(tipo, filtrosAplicados);
      saveAxiosBlobAs(resposta, `relatorio-acessos-${tipo}.xlsx`);
    } catch (e) {
      console.error("Erro ao baixar relatório:", e);
      message.error("Não foi possível baixar o relatório");
    } finally {
      setExportando(false);
    }
  };

  if (!relatorio) {
    return (
      <div>
        <Header style={{ marginBottom: "2rem" }} mostrarPesquisar={false} />
        <main className="analise-acessos">
          <p>Relatório não encontrado.</p>
          <Link to="/dashboard" className="report-link">
            <FaArrowLeft /> Voltar ao dashboard
          </Link>
        </main>
      </div>
    );
  }

  const colunas = 3 + (relatorio.caminho ? 1 : 0) + (relatorio.percentual ? 1 : 0);

  return (
    <div>
      <Header style={{ marginBottom: "2rem" }} mostrarPesquisar={false} />

      <main className="analise-acessos">
        <Link to={`/dashboard?${filtrosParaQuery(filtrosAplicados)}`} className="report-link voltar">
          <FaArrowLeft /> Voltar ao dashboard
        </Link>

        <section className="page-header">
          <h1>{relatorio.titulo}</h1>
          <p>
            Relatório completo do período {formatarData(filtrosAplicados.dataInicio)} –{" "}
            {formatarData(filtrosAplicados.dataFim)}.
          </p>
        </section>

        <FiltrosAnalise
          filtros={filtros}
          setFiltros={setFiltros}
          onAtualizar={aoAtualizar}
          carregando={carregando}
        >
          <button
            type="button"
            className="btn btn-outline"
            onClick={aoBaixarExcel}
            disabled={exportando || totalItens === 0}
          >
            <FaFileExcel /> {exportando ? "Baixando..." : "Baixar Excel"}
          </button>
        </FiltrosAnalise>

        <div className="table-container">
          <table className="participants-table">
            <thead>
              <tr>
                <th>Posição</th>
                <th>{relatorio.coluna}</th>
                {relatorio.caminho && <th>Caminho</th>}
                <th>{relatorio.unidade}</th>
                {relatorio.percentual && <th>Percentual</th>}
              </tr>
            </thead>
            <tbody>
              {lista.length > 0 ? (
                lista.map((item) => (
                  <tr key={item.posicao}>
                    <td>{item.posicao}</td>
                    <td>{item.nome}</td>
                    {relatorio.caminho && <td>{item.caminho || "-"}</td>}
                    <td>{formatarNumero(item.quantidade)}</td>
                    {relatorio.percentual && <td>{formatarNumero(item.percentual)}%</td>}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={colunas} className="text-center">
                    {carregando ? "Carregando..." : "Sem dados no período"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPaginas > 1 && (
          <div className="pagination">
            <button
              className="pagination-button"
              onClick={() => carregar(filtrosAplicados, paginaAtual - 1)}
              disabled={paginaAtual <= 1}
            >
              Anterior
            </button>

            <div className="pagination-numbers">
              {[...Array(totalPaginas)].map((_, indice) => (
                <button
                  key={indice + 1}
                  className={`pagination-number ${paginaAtual === indice + 1 ? "active" : ""}`}
                  onClick={() => carregar(filtrosAplicados, indice + 1)}
                >
                  {indice + 1}
                </button>
              ))}
            </div>

            <button
              className="pagination-button"
              onClick={() => carregar(filtrosAplicados, paginaAtual + 1)}
              disabled={paginaAtual >= totalPaginas}
            >
              Próximo
            </button>
          </div>
        )}

        {totalItens > 0 && (
          <p className="text-muted" style={{ fontSize: "12px" }}>
            {totalItens} registro(s) encontrado(s)
          </p>
        )}
      </main>
    </div>
  );
}
